import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import {
  getCurrentUserPerms,
  hasWrite,
  isCurrentUserAdmin,
} from "../../utils/perm";
import {
  createTax,
  deleteTax,
  getAllTaxes,
  updateTax,
} from "../../api/taxApi";

export const Tax = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteTax =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "products");

  const [taxRows, setTaxRows] = useState([]);
  const [selectedTaxId, setSelectedTaxId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [taxName, setTaxName] = useState({ en: "", ar: "" });
  const [taxType, setTaxType] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [status, setStatus] = useState(true);

  const [editTax, setEditTax] = useState({
    id: "",
    taxName: { en: "", ar: "" },
    taxType: "",
    taxRate: "",
    status: true,
  });

  const currentLanguage = localStorage.getItem("lang") || "en";

  const fetchTaxes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllTaxes({
        page: 1,
        limit: 100,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      setTaxRows(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      toast.error(error?.message || window.translate("Failed to fetch taxes"));
      setTaxRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaxes();
  }, [fetchTaxes]);

  const tableData = useMemo(
    () =>
      taxRows.map((row, index) => ({
        key: row._id || row.id || index,
        id: row._id || row.id,
        tax: row.taxName?.[currentLanguage] || row.taxName?.en || "Unnamed",
        taxtype: row.taxType || "-",
        taxvalue: `${Number(row.taxRate || 0)}%`,
        noofproducts: row.productCount || 0,
        createdon: row.createdAt
          ? new Date(row.createdAt).toLocaleDateString()
          : "-",
        status: row.status ? "Active" : "Inactive",
        originalData: row,
      })),
    [currentLanguage, taxRows]
  );

  const resetAddForm = () => {
    setTaxName({ en: "", ar: "" });
    setTaxType("");
    setTaxRate("");
    setStatus(true);
  };

  const isValidRate = (value) => {
    const numericRate = Number(value);
    return !Number.isNaN(numericRate) && numericRate >= 0 && numericRate <= 100;
  };

  const handleAddTax = async (e) => {
    e.preventDefault();

    if (!canWriteTax) {
      toast.error(
        `${window.translate("You don't have permission to modify products")}`
      );
      return;
    }

    if (submitting) return;

    if (
      !taxName.en.trim() ||
      !taxName.ar.trim() ||
      !taxType.trim() ||
      !isValidRate(taxRate)
    ) {
      toast.error(`${window.translate("Please provide valid tax details")}`);
      return;
    }

    try {
      setSubmitting(true);

      await createTax({
        taxName: {
          en: taxName.en.trim(),
          ar: taxName.ar.trim(),
        },
        taxType: taxType.trim(),
        taxRate: Number(taxRate),
        status,
      });

      toast.success(`${window.translate("Tax created successfully!")}`);
      resetAddForm();
      document.getElementById("close-add-tax-btn")?.click();
      await fetchTaxes();
    } catch (error) {
      toast.error(error?.message || window.translate("Failed to create tax"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (row) => {
    setEditTax({
      id: row._id || row.id || "",
      taxName: {
        en: row.taxName?.en || "",
        ar: row.taxName?.ar || "",
      },
      taxType: row.taxType || "",
      taxRate: row.taxRate ?? "",
      status: Boolean(row.status),
    });
  };

  const handleUpdateTax = async () => {
    if (!canWriteTax) {
      toast.error(
        `${window.translate("You don't have permission to modify products")}`
      );
      return;
    }

    if (
      !editTax.taxName.en.trim() ||
      !editTax.taxName.ar.trim() ||
      !editTax.taxType.trim() ||
      !isValidRate(editTax.taxRate)
    ) {
      toast.error(`${window.translate("Please provide valid tax details")}`);
      return;
    }

    try {
      setSubmitting(true);

      await updateTax(editTax.id, {
        taxName: {
          en: editTax.taxName.en.trim(),
          ar: editTax.taxName.ar.trim(),
        },
        taxType: editTax.taxType.trim(),
        taxRate: Number(editTax.taxRate),
        status: Boolean(editTax.status),
      });

      toast.success(`${window.translate("Tax updated successfully")}`);
      document.getElementById("close-edit-tax-btn")?.click();
      await fetchTaxes();
    } catch (error) {
      toast.error(error?.message || window.translate("Failed to update tax"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTax = async () => {
    if (!canWriteTax) {
      toast.error(
        `${window.translate("You don't have permission to modify products")}`
      );
      return;
    }

    if (!selectedTaxId) return;

    try {
      await deleteTax(selectedTaxId);
      toast.success(`${window.translate("Tax deleted successfully!")}`);
      setSelectedTaxId(null);
      await fetchTaxes();
    } catch (error) {
      toast.error(error?.message || window.translate("Failed to delete tax"));
    }
  };

  const columns = [
    {
      title: window.translate("Tax"),
      dataIndex: "tax",
      sorter: (a, b) => a.tax.localeCompare(b.tax),
    },
    {
      title: window.translate("Tax Type"),
      dataIndex: "taxtype",
      sorter: (a, b) => a.taxtype.localeCompare(b.taxtype),
    },
    {
      title: window.translate("Tax Value"),
      dataIndex: "taxvalue",
      sorter: (a, b) =>
        Number(String(a.taxvalue).replace("%", "")) -
        Number(String(b.taxvalue).replace("%", "")),
    },
    {
      title: window.translate("No of Products"),
      dataIndex: "noofproducts",
      sorter: (a, b) => a.noofproducts - b.noofproducts,
    },
    {
      title: window.translate("Created Date"),
      dataIndex: "createdon",
      sorter: (a, b) => a.createdon.localeCompare(b.createdon),
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => (
        <span
          className={`badge table-badge fw-medium fs-10 ${
            text.toLowerCase() === "inactive" ? "bg-danger" : "bg-success"
          }`}
        >
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) =>
        canWriteTax ? (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#edit-tax"
                onClick={() => handleEditClick(record.originalData)}
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
              <Link
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                className="p-2"
                to="#"
                onClick={() => setSelectedTaxId(record.id)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            </div>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Tax")}</h4>
                <h6>{window.translate("Manage product taxes")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            {canWriteTax && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add-tax"
                >
                  <i className="ti ti-circle-plus me-2 ms-2"></i>{" "}
                  {window.translate("Add Tax")}
                </Link>
              </div>
            )}
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-4 fw-medium text-muted">
                    {window.translate("Loading taxes...")}
                  </div>
                ) : tableData.length > 0 ? (
                  <Table columns={columns} dataSource={tableData} />
                ) : (
                  <div className="text-center py-4 fw-medium text-muted">
                    {window.translate("No tax records found.")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <div className="modal fade" id="add-tax">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Add Tax")}</h4>
                  </div>
                  <button
                    id="close-add-tax-btn"
                    type="button"
                    className="close bg-danger text-white fs-16"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleAddTax}>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax")} (English)
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={taxName.en}
                        onChange={(e) =>
                          setTaxName((prev) => ({
                            ...prev,
                            en: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax")} (Arabic)
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={taxName.ar}
                        onChange={(e) =>
                          setTaxName((prev) => ({
                            ...prev,
                            ar: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax Type")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={taxType}
                        onChange={(e) => setTaxType(e.target.value)}
                        placeholder="Enter Tax Type (e.g. Sales Tax, VAT)"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax Value")} (%)
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={taxRate}
                        min="0"
                        max="100"
                        step="0.01"
                        onChange={(e) => setTaxRate(e.target.value)}
                      />
                    </div>

                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">
                          {window.translate("status")}
                        </span>
                        <input
                          type="checkbox"
                          id="add-tax-status"
                          className="check"
                          checked={status}
                          onChange={() => setStatus((prev) => !prev)}
                        />
                        <label
                          htmlFor="add-tax-status"
                          className="checktoggle"
                        />
                      </div>
                    </div>

                    <div className="modal-footer mt-3">
                      <button
                        type="button"
                        className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                        data-bs-dismiss="modal"
                      >
                        {window.translate("cancel")}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary fs-13 fw-medium p-2 px-3"
                        disabled={submitting}
                      >
                        {submitting
                          ? window.translate("processing")
                          : window.translate("Add Tax")}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="edit-tax">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Edit Tax")}</h4>
                  </div>
                  <button
                    id="close-edit-tax-btn"
                    type="button"
                    className="close bg-danger text-white fs-16"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax")} EN
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editTax.taxName.en}
                        onChange={(e) =>
                          setEditTax((prev) => ({
                            ...prev,
                            taxName: { ...prev.taxName, en: e.target.value },
                          }))
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax")} AR
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editTax.taxName.ar}
                        onChange={(e) =>
                          setEditTax((prev) => ({
                            ...prev,
                            taxName: { ...prev.taxName, ar: e.target.value },
                          }))
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax Type")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editTax.taxType}
                        onChange={(e) =>
                          setEditTax((prev) => ({
                            ...prev,
                            taxType: e.target.value,
                          }))
                        }
                        placeholder="Enter Tax Type"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Tax Value")} (%)
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editTax.taxRate}
                        onChange={(e) =>
                          setEditTax((prev) => ({
                            ...prev,
                            taxRate: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">
                          {window.translate("status")}
                        </span>
                        <input
                          type="checkbox"
                          id="edit-tax-status"
                          className="check"
                          checked={Boolean(editTax.status)}
                          onChange={() =>
                            setEditTax((prev) => ({
                              ...prev,
                              status: !prev.status,
                            }))
                          }
                        />
                        <label
                          htmlFor="edit-tax-status"
                          className="checktoggle"
                        />
                      </div>
                    </div>
                  </form>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("cancel")}
                  </button>
                  <button
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleUpdateTax}
                    type="button"
                    disabled={submitting}
                  >
                    {window.translate("Save Changes")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CommonDeleteModal
        title={window.translate("Delete Tax")}
        onConfirm={handleDeleteTax}
        onCancel={() => setSelectedTaxId(null)}
      />
    </>
  );
};